import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, RefreshCw, Inbox, Link2, ChevronRight, X, CheckCircle2,
  AlertTriangle, ExternalLink, Upload, Webhook, Wand2,
} from "lucide-react";
import { Link } from "react-router-dom";

const CONFIDENCE_TONE = {
  high: { bg: "bg-sage-500/10", text: "text-sage-700", border: "border-sage-200", label: "High" },
  medium: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200", label: "Medium" },
  low: { bg: "bg-terracotta-50", text: "text-terracotta-700", border: "border-terracotta-100", label: "Low" },
  none: { bg: "bg-paper-100", text: "text-ink-500", border: "border-paper-200", label: "No match" },
};

const TARGET_FIELDS = [
  { key: "complaint", label: "Chief complaint", hint: "→ session intake_notes" },
  { key: "medical_history", label: "Medical history", hint: "→ client medical_history" },
  { key: "medications", label: "Medications", hint: "→ visible in raw payload" },
  { key: "long_term_goals", label: "Long-term goals", hint: "→ client long_term_goals" },
  { key: "pain_vas", label: "Pain VAS (0–10)", hint: "→ session outcome.pain_before" },
  { key: "consent", label: "Consent/signature", hint: "→ stored flag" },
];

export default function JotformIntegration() {
  const [tab, setTab] = useState("connections");
  const [connections, setConnections] = useState([]);
  const [forms, setForms] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddConn, setShowAddConn] = useState(false);
  const [editingForm, setEditingForm] = useState(null); // {connection_id, jotform_form_id, jotform_title} for new
  const [clients, setClients] = useState([]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, f, q, cl] = await Promise.all([
        api.get("/jotform/connections"),
        api.get("/jotform/forms"),
        api.get("/jotform/queue?status=pending"),
        api.get("/clients"),
      ]);
      setConnections(c.data);
      setForms(f.data);
      setQueue(q.data);
      setClients(cl.data);
    } catch (e) {
      toast.error("Could not load Jotform data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const sync = async (formId) => {
    try {
      const r = await api.post(`/jotform/forms/${formId}/sync`);
      toast.success(`Pulled ${r.data.pulled} submissions`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    }
  };

  const removeConnection = async (id) => {
    if (!window.confirm("Remove this Jotform connection? Saved forms will also be removed (submissions kept).")) return;
    try {
      await api.delete(`/jotform/connections/${id}`);
      toast.success("Connection removed");
      await loadAll();
    } catch {
      toast.error("Could not remove");
    }
  };

  const removeForm = async (id) => {
    if (!window.confirm("Remove this saved form?")) return;
    try {
      await api.delete(`/jotform/forms/${id}`);
      toast.success("Removed");
      await loadAll();
    } catch {
      toast.error("Could not remove");
    }
  };

  const registerWebhook = async (formId) => {
    try {
      const r = await api.post(`/jotform/forms/${formId}/register-webhook`);
      toast.success(`Webhook registered: ${r.data.webhook_url || "ok"}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not register webhook");
    }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2 text-sage-700">Intake form integration</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-ink-900">Jotform</h1>
          <p className="text-ink-700 mt-3 max-w-2xl">
            Connect your Jotform account, choose which intake forms to import, and Posturra will auto-attach
            submissions to the right client (with a confidence score). Manual review queue for anything ambiguous.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="jotform-tabs">
        <TabBtn label="Connections" count={connections.length} active={tab === "connections"} onClick={() => setTab("connections")} testid="tab-connections" />
        <TabBtn label="Imported forms" count={forms.length} active={tab === "forms"} onClick={() => setTab("forms")} testid="tab-forms" />
        <TabBtn label={`Inbox`} count={queue.length} active={tab === "inbox"} onClick={() => setTab("inbox")} testid="tab-inbox" badge={queue.length > 0} />
      </div>

      {loading && <div className="text-ink-500">Loading…</div>}

      {tab === "connections" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-ink-700">Add your Jotform API key. We'll validate it against Jotform's <code>/user</code> endpoint.</div>
            <button
              onClick={() => setShowAddConn(true)}
              data-testid="add-connection-btn"
              className="min-h-[44px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Connect Jotform
            </button>
          </div>
          {connections.length === 0 ? (
            <div className="bg-white border border-paper-200 rounded-3xl p-12 text-center" data-testid="connections-empty">
              <Link2 className="w-10 h-10 mx-auto text-ink-300 mb-3" />
              <div className="font-heading text-xl text-ink-900 mb-2">No Jotform connection yet</div>
              <p className="text-ink-700 max-w-md mx-auto mb-4">
                Get your API key at <a className="underline" href="https://www.jotform.com/myaccount/api" target="_blank" rel="noreferrer">Jotform → My Account → API <ExternalLink className="w-3 h-3 inline" /></a> (Settings → API → Create New Key with Full Access).
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {connections.map((c) => (
                <ConnectionCard key={c.id} c={c} onRemove={() => removeConnection(c.id)} onPickForms={() => setEditingForm({ connection_id: c.id })} />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "forms" && (
        <FormsList forms={forms} connections={connections} onSync={sync} onRemove={removeForm} onWebhook={registerWebhook} onEdit={(f) => setEditingForm({ ...f, edit: true })} onAdd={(cid) => setEditingForm({ connection_id: cid })} />
      )}

      {tab === "inbox" && (
        <Inbox_ queue={queue} clients={clients} onChange={loadAll} />
      )}

      {showAddConn && (
        <AddConnectionModal onClose={() => setShowAddConn(false)} onSaved={() => { setShowAddConn(false); loadAll(); }} />
      )}

      {editingForm && (
        <FormPickerModal
          payload={editingForm}
          connections={connections}
          onClose={() => setEditingForm(null)}
          onSaved={() => { setEditingForm(null); loadAll(); }}
        />
      )}
    </Layout>
  );
}

function TabBtn({ label, count, active, onClick, testid, badge }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`min-h-[40px] px-4 rounded-full text-sm inline-flex items-center gap-2 transition-all ${
        active ? "bg-ink-900 text-white" : "text-ink-700 hover:bg-paper-100 border border-paper-200"
      }`}
    >
      {label}
      <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-medium ${
        badge ? "bg-terracotta-500 text-white" : active ? "bg-white/20 text-white" : "bg-paper-100 text-ink-700"
      }`}>{count}</span>
    </button>
  );
}

function ConnectionCard({ c, onRemove, onPickForms }) {
  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-5 flex items-center gap-4 flex-wrap" data-testid={`connection-${c.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="font-heading text-lg text-ink-900">{c.label}</div>
        <div className="text-xs text-ink-500 mt-1 flex items-center gap-2">
          <code className="px-1.5 py-0.5 rounded bg-paper-100">{c.api_key_masked}</code>
          <span>· {c.base_url.replace("https://", "")}</span>
        </div>
      </div>
      <button onClick={onPickForms} className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm inline-flex items-center gap-2" data-testid={`pick-forms-${c.id}`}>
        <Plus className="w-3.5 h-3.5" /> Import a form
      </button>
      <button onClick={onRemove} className="w-9 h-9 rounded-full hover:bg-paper-100 hover:text-destructive flex items-center justify-center text-ink-500" data-testid={`remove-connection-${c.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function FormsList({ forms, connections, onSync, onRemove, onWebhook, onEdit, onAdd }) {
  if (forms.length === 0) {
    return (
      <div className="bg-white border border-paper-200 rounded-3xl p-12 text-center" data-testid="forms-empty">
        <div className="font-heading text-xl text-ink-900 mb-2">No imported forms yet</div>
        <p className="text-ink-700 max-w-md mx-auto mb-4">Go to the Connections tab → "Import a form" to pick a Jotform form and map its fields.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {forms.map((f) => (
        <div key={f.id} className="bg-white border border-paper-200 rounded-2xl p-5" data-testid={`form-${f.id}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="font-heading text-lg text-ink-900">{f.label}</div>
              <div className="text-xs text-ink-500 mt-1 flex items-center gap-2 flex-wrap">
                <code className="px-1.5 py-0.5 rounded bg-paper-100">form {f.jotform_form_id}</code>
                <span>· auto-attach ≥ <span className="font-medium">{f.auto_attach_min_confidence}</span></span>
                {f.last_synced_at && <span>· synced {new Date(f.last_synced_at).toLocaleString()}</span>}
                {f.webhook_registered_at && <span className="text-sage-700 inline-flex items-center gap-1"><Webhook className="w-3 h-3" /> webhook active</span>}
              </div>
            </div>
            <button onClick={() => onSync(f.id)} className="min-h-[40px] px-4 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm inline-flex items-center gap-2" data-testid={`sync-${f.id}`}>
              <RefreshCw className="w-3.5 h-3.5" /> Sync now
            </button>
            <button onClick={() => onWebhook(f.id)} className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm inline-flex items-center gap-2" data-testid={`webhook-${f.id}`}>
              <Webhook className="w-3.5 h-3.5" /> {f.webhook_registered_at ? "Re-register webhook" : "Enable webhook"}
            </button>
            <button onClick={() => onEdit(f)} className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm" data-testid={`edit-${f.id}`}>Edit</button>
            <button onClick={() => onRemove(f.id)} className="w-9 h-9 rounded-full hover:bg-paper-100 hover:text-destructive flex items-center justify-center text-ink-500" data-testid={`remove-${f.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
      {connections.length > 0 && (
        <button
          onClick={() => onAdd(connections[0].id)}
          className="w-full min-h-[48px] rounded-2xl border-2 border-dashed border-paper-200 hover:border-sage-500/40 text-ink-700 text-sm inline-flex items-center justify-center gap-2"
          data-testid="add-another-form"
        >
          <Plus className="w-4 h-4" /> Import another form
        </button>
      )}
    </div>
  );
}

function AddConnectionModal({ onClose, onSaved }) {
  const [label, setLabel] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("https://api.jotform.com");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/jotform/connections", { label: label.trim() || "My Jotform", api_key: apiKey.trim(), base_url: baseUrl });
      toast.success("Jotform connected");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="add-connection-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg p-6 sm:p-8 relative">
        <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full hover:bg-paper-100 flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
        <div className="label-overline mb-2">Add Jotform connection</div>
        <h2 className="font-heading text-2xl text-ink-900 mb-4">Paste your API key</h2>
        <p className="text-sm text-ink-700 mb-4">
          Open <a className="underline" href="https://www.jotform.com/myaccount/api" target="_blank" rel="noreferrer">Jotform → My Account → API</a> and
          create a new key with <b>Full Access</b>. We validate it before saving.
        </p>
        <div className="space-y-3">
          <div>
            <label className="label-overline block mb-1">Label</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="My clinic" className="w-full min-h-[44px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none" data-testid="conn-label" />
          </div>
          <div>
            <label className="label-overline block mb-1">API key</label>
            <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="••••••••••••" className="w-full min-h-[44px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none font-mono text-sm" data-testid="conn-api-key" />
          </div>
          <div>
            <label className="label-overline block mb-1">Base URL</label>
            <select value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} className="w-full min-h-[44px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none" data-testid="conn-base-url">
              <option value="https://api.jotform.com">Standard (api.jotform.com)</option>
              <option value="https://eu-api.jotform.com">EU (eu-api.jotform.com)</option>
              <option value="https://hipaa-api.jotform.com">HIPAA (hipaa-api.jotform.com)</option>
            </select>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="min-h-[44px] px-5 rounded-full text-ink-700 hover:bg-paper-100">Cancel</button>
          <button onClick={save} disabled={busy || !apiKey.trim()} className="min-h-[44px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium disabled:opacity-50" data-testid="conn-save">
            {busy ? "Validating…" : "Connect"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormPickerModal({ payload, onClose, onSaved }) {
  const isEdit = !!payload.edit;
  const [step, setStep] = useState(isEdit ? "map" : "pick");
  const [remoteForms, setRemoteForms] = useState([]);
  const [selected, setSelected] = useState(payload.jotform_form_id ? { id: payload.jotform_form_id, title: payload.label } : null);
  const [questions, setQuestions] = useState([]);
  const [mapping, setMapping] = useState(payload.field_mapping || {});
  const [label, setLabel] = useState(payload.label || "");
  const [autoAttach, setAutoAttach] = useState(payload.auto_attach !== false);
  const [autoAttachMin, setAutoAttachMin] = useState(payload.auto_attach_min_confidence || "high");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (step !== "pick") return;
      try {
        const r = await api.get(`/jotform/connections/${payload.connection_id}/forms`);
        setRemoteForms(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not list Jotform forms");
      }
    })();
  }, [step, payload.connection_id]);

  useEffect(() => {
    (async () => {
      if (step !== "map" || !selected?.id) return;
      try {
        const r = await api.get(`/jotform/connections/${payload.connection_id}/forms/${selected.id}/questions`);
        setQuestions(r.data.questions);
        if (!isEdit) setMapping(r.data.suggested_mapping || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load questions");
      }
    })();
  }, [step, selected, payload.connection_id, isEdit]);

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/jotform/forms/${payload.id}`, {
          label: label || selected.title,
          field_mapping: mapping,
          auto_attach: autoAttach,
          auto_attach_min_confidence: autoAttachMin,
        });
      } else {
        await api.post("/jotform/forms", {
          connection_id: payload.connection_id,
          form_id: selected.id,
          label: label || selected.title,
          field_mapping: mapping,
          auto_attach: autoAttach,
          auto_attach_min_confidence: autoAttachMin,
        });
      }
      toast.success(isEdit ? "Form updated" : "Form imported");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="form-picker-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl my-8 p-6 sm:p-8 relative">
        <button onClick={onClose} className="absolute top-5 right-5 w-9 h-9 rounded-full hover:bg-paper-100 flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
        <div className="label-overline mb-2">{isEdit ? "Edit form mapping" : "Pick a Jotform"}</div>
        {step === "pick" && (
          <>
            <h2 className="font-heading text-2xl text-ink-900 mb-4">Which intake form do you want to import?</h2>
            <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="remote-forms-list">
              {remoteForms.length === 0 && <div className="text-ink-500 text-sm">No forms found on this Jotform account.</div>}
              {remoteForms.map((f) => (
                <button
                  key={f.id}
                  onClick={() => { setSelected(f); setLabel(f.title); setStep("map"); }}
                  className="w-full text-left bg-paper-50 hover:bg-paper-100 border border-paper-200 rounded-xl p-3 flex items-center gap-3"
                  data-testid={`remote-form-${f.id}`}
                >
                  <div className="flex-1">
                    <div className="font-medium text-ink-900">{f.title}</div>
                    <div className="text-xs text-ink-500">{f.id} · {f.count || 0} submissions</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-ink-500" />
                </button>
              ))}
            </div>
          </>
        )}

        {step === "map" && selected && (
          <>
            <h2 className="font-heading text-2xl text-ink-900 mb-1">Map fields → Posturra</h2>
            <p className="text-sm text-ink-700 mb-4">Tell Posturra which Jotform question maps to each clinical field. We pre-filled with our best guess.</p>
            <div className="space-y-3 mb-5 max-h-[360px] overflow-y-auto">
              {TARGET_FIELDS.map((t) => (
                <div key={t.key} className="flex items-center gap-3 flex-wrap">
                  <div className="w-44 shrink-0">
                    <div className="text-sm font-medium text-ink-900">{t.label}</div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-500">{t.hint}</div>
                  </div>
                  <select
                    value={mapping[t.key] || ""}
                    onChange={(e) => setMapping({ ...mapping, [t.key]: e.target.value || null })}
                    className="flex-1 min-h-[40px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none text-sm"
                    data-testid={`map-${t.key}`}
                  >
                    <option value="">— skip —</option>
                    {questions.map((q) => (
                      <option key={q.qid} value={q.qid}>{q.label || q.name} ({q.type?.replace("control_", "")})</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <div>
                <label className="label-overline block mb-1">Form label</label>
                <input value={label} onChange={(e) => setLabel(e.target.value)} className="w-full min-h-[40px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none" data-testid="form-label" />
              </div>
              <div>
                <label className="label-overline block mb-1">Auto-attach min confidence</label>
                <select value={autoAttachMin} onChange={(e) => setAutoAttachMin(e.target.value)} className="w-full min-h-[40px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none" data-testid="auto-attach-min">
                  <option value="high">High (email exact)</option>
                  <option value="medium">Medium or higher (phone exact)</option>
                  <option value="low">Low or higher (fuzzy name)</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={autoAttach} onChange={(e) => setAutoAttach(e.target.checked)} className="w-4 h-4" data-testid="auto-attach-toggle" />
                Auto-attach matching submissions
              </label>
            </div>
            <div className="flex items-center justify-end gap-2">
              {!isEdit && <button onClick={() => setStep("pick")} className="min-h-[44px] px-5 rounded-full text-ink-700 hover:bg-paper-100">Back</button>}
              <button onClick={save} disabled={busy} className="min-h-[44px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium disabled:opacity-50" data-testid="form-save">
                <Wand2 className="w-4 h-4 inline mr-1" /> {busy ? "Saving…" : isEdit ? "Save changes" : "Import this form"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Inbox_({ queue, clients, onChange }) {
  if (queue.length === 0) {
    return (
      <div className="bg-white border border-paper-200 rounded-3xl p-12 text-center" data-testid="inbox-empty">
        <Inbox className="w-10 h-10 mx-auto text-ink-300 mb-3" />
        <div className="font-heading text-xl text-ink-900 mb-2">Inbox empty</div>
        <p className="text-ink-700 max-w-md mx-auto">Submissions awaiting manual client match will appear here. Anything auto-attached goes straight onto the matched client's record.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="inbox-list">
      {queue.map((s) => (
        <SubmissionRow key={s.id} s={s} clients={clients} onChange={onChange} />
      ))}
    </div>
  );
}

function SubmissionRow({ s, clients, onChange }) {
  const [open, setOpen] = useState(false);
  const [pickClient, setPickClient] = useState(s.match_client_id || "");
  const tone = CONFIDENCE_TONE[s.match_confidence] || CONFIDENCE_TONE.none;
  const parsed = s.parsed || {};

  const attach = async (createNew = false) => {
    try {
      await api.post(`/jotform/submissions/${s.id}/attach`, {
        client_id: createNew ? null : (pickClient || s.match_client_id || null),
        create_session_draft: false,
      });
      toast.success(createNew ? "Created new client + attached" : "Attached to client");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not attach");
    }
  };

  const discard = async () => {
    if (!window.confirm("Discard this submission?")) return;
    try {
      await api.delete(`/jotform/submissions/${s.id}`);
      toast.success("Discarded");
      onChange();
    } catch {
      toast.error("Could not discard");
    }
  };

  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-5" data-testid={`submission-${s.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <div className="font-heading text-lg text-ink-900">{parsed.name || "Unnamed submission"}</div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${tone.bg} ${tone.text} ${tone.border}`} data-testid={`confidence-${s.id}`}>
              {tone.label} confidence
            </span>
          </div>
          <div className="text-xs text-ink-500 flex items-center gap-3 flex-wrap">
            {parsed.email && <span>{parsed.email}</span>}
            {parsed.phone && <span>{parsed.phone}</span>}
            {parsed.pain_before != null && <span>VAS {parsed.pain_before}/10</span>}
            <span className="text-ink-400">· received {new Date(s.created_at).toLocaleString()}</span>
          </div>
          {s.match_reason && <div className="text-xs text-ink-500 mt-1 italic">{s.match_reason}</div>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={pickClient} onChange={(e) => setPickClient(e.target.value)} className="min-h-[40px] px-3 rounded-full bg-paper-50 border border-paper-200 outline-none text-sm" data-testid={`pick-client-${s.id}`}>
            <option value="">— pick a client —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={() => attach(false)} disabled={!pickClient} className="min-h-[40px] px-4 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`attach-${s.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Attach
          </button>
          <button onClick={() => attach(true)} className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm inline-flex items-center gap-1.5" data-testid={`create-new-${s.id}`}>
            <Plus className="w-3.5 h-3.5" /> New client
          </button>
          <button onClick={discard} className="w-9 h-9 rounded-full hover:bg-paper-100 hover:text-destructive flex items-center justify-center text-ink-500" data-testid={`discard-${s.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setOpen((v) => !v)} className="w-9 h-9 rounded-full hover:bg-paper-100 text-ink-500 inline-flex items-center justify-center" data-testid={`expand-${s.id}`}>
            <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-4 pt-4 border-t border-paper-200 space-y-2 text-sm" data-testid={`detail-${s.id}`}>
          {parsed.intake_notes && (
            <div>
              <div className="label-overline mb-1">Intake notes (parsed)</div>
              <div className="bg-paper-50 rounded-xl p-3 whitespace-pre-wrap text-ink-700 text-sm">{parsed.intake_notes}</div>
            </div>
          )}
          {parsed.medical_history?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <span className="text-xs text-ink-500 mr-2">Medical history:</span>
              {parsed.medical_history.map((m, i) => <span key={`${m}-${i}`} className="text-xs px-2 py-0.5 rounded-full bg-terracotta-50 text-terracotta-700">{m}</span>)}
            </div>
          )}
          {parsed.long_term_goals?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <span className="text-xs text-ink-500 mr-2">Goals:</span>
              {parsed.long_term_goals.map((m, i) => <span key={`${m}-${i}`} className="text-xs px-2 py-0.5 rounded-full bg-sage-50 text-sage-700">{m}</span>)}
            </div>
          )}
          {parsed.field_log?.length > 0 && (
            <details className="text-xs text-ink-500">
              <summary className="cursor-pointer">All fields (raw)</summary>
              <pre className="bg-paper-50 rounded-xl p-3 mt-2 overflow-x-auto text-[11px]">{JSON.stringify(parsed.field_log, null, 2)}</pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
